import { useEffect, useState } from "react";
import Papa from "papaparse";
import SkeletonMobileMindshare from "../components/skeletons/SkeletonMobileMindshare";
import VirtualizedDropdown from "../components/VirtualizedDropdown/VirtualizedDropdown";
import TextInputField from "../components/Fields/TextInputField";
import SavedViewsManager from "../components/SavedViewsManager";

function MobileMindshare() {
  const [games, setGames] = useState([]);
  const [displayedGames, setDisplayedGames] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedPublisher, setSelectedPublisher] = useState(null);
  const [allTags, setAllTags] = useState([]);
  const [allPublishers, setAllPublishers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, metaRes] = await Promise.all([
          fetch(
            `https://mindgamedataapi.s3.us-east-1.amazonaws.com/Mobile_Latest_Data.csv?t=${Date.now()}`
          ),
          fetch(
            `https://mindgamedataapi.s3.us-east-1.amazonaws.com/Mobile_Metadata.csv?t=${Date.now()}`
          ),
        ]);
        const [statsText, metaText] = await Promise.all([
          statsRes.text(),
          metaRes.text(),
        ]);
        const statsData = Papa.parse(statsText.trim(), {
          header: true,
          skipEmptyLines: true,
        }).data;
        const metaData = Papa.parse(metaText.trim(), {
          header: true,
          skipEmptyLines: true,
        }).data;

        const metaMap = {};
        const tagSet = new Set();
        const publisherSet = new Set();

        for (const meta of metaData) {
          metaMap[meta.Game] = meta;
          if (meta.tagsGames)
            meta.tagsGames.split(",").forEach((tag) => tagSet.add(tag.trim()));
          if (meta.Publisher) publisherSet.add(meta.Publisher.trim());
        }

        const latestMap = {};
        for (const stat of statsData) {
          const period = new Date(stat.Period);
          if (
            !latestMap[stat.Game] ||
            new Date(latestMap[stat.Game].Period) < period
          ) {
            latestMap[stat.Game] = stat;
          }
        }

        const combined = Object.values(latestMap)
          .map((stat) => {
            const meta = metaMap[stat.Game];
            if (!meta || !stat["Mobile mindSHARE"]) return null;

            const growth = parseFloat(stat["Growth PT"] || 0);
            let glow = "";
            if (growth > 0.005) glow = "rgba(63, 169, 245, 0.95)";
            else if (growth > 0.001) glow = "rgba(63, 169, 245, 0.75)";
            else if (growth > 0.0001) glow = "rgba(63, 169, 245, 0.55)";
            else if (growth > 0.000001) glow = "rgba(63, 169, 245, 0.35)";

            return {
              game: stat.Game,
              mindshare: parseFloat(stat["Mobile mindSHARE"]),
              growth,
              glow,
              image: meta.ios_icon_url?.trim() || "",
              tags: meta.tagsGames?.split(",").map((t) => t.trim()) || [],
              publisher: meta.Publisher?.trim() || "",
            };
          })
          .filter(Boolean);

        combined.sort((a, b) => b.mindshare - a.mindshare);
        setGames(combined);
        setDisplayedGames(combined);
        setAllTags([...tagSet].sort());
        setAllPublishers([...publisherSet].sort());
      } catch (err) {
        console.error("Failed to load mobile mindSHARE data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <SkeletonMobileMindshare gamesCount={64} />;
  }

  const filtered = displayedGames
    .filter((g) => g.game.toLowerCase().includes(searchTerm.toLowerCase()))
    .filter((g) => !selectedTag || g.tags.includes(selectedTag.value))
    .filter(
      (g) => !selectedPublisher || g.publisher === selectedPublisher.value
    );

  const totalMindshare = filtered.reduce(
    (sum, g) => sum + (g.mindshare || 0),
    0
  );
  const maxMindshare = Math.max(...filtered.map((g) => g.mindshare || 0));

  return (
    <div
      style={{
        display: "flex",
        background: "#000",
        color: "#fff",
        minHeight: "calc(var(--mg-vh) * 100)",
      }}
    >
      <div
        style={{
          position: "sticky",
          top: 0,
          height: "calc(var(--mg-vh) * 100)",
          overflowY: "auto",
          padding: 20,
          background: "#111",
          borderRight: "1px solid #333",
          width: 220,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <div style={{ fontWeight: "bold", color: "#3fa9f5" }}>Filters</div>

        <SavedViewsManager
          type="mobile"
          currentFilters={{
            searchTerm: searchTerm,
            tag: selectedTag,
            publisher: selectedPublisher,
          }}
          onApplyFilters={(filters) => {
            setSearchTerm(filters.searchTerm ?? "");
            setSelectedTag(filters.tag ?? null);
            setSelectedPublisher(filters.publisher ?? null);
          }}
          onClearFilters={() => {
            setSearchTerm("");
            setSelectedTag(null);
            setSelectedPublisher(null);
          }}
        />

        <TextInputField
          placeholder="Search games..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <VirtualizedDropdown
          placeholder="All Tags"
          value={selectedTag}
          onChange={setSelectedTag}
          options={allTags.map((t) => ({ label: t, value: t }))}
          isClearable
        />
        <VirtualizedDropdown
          placeholder="All Publishers"
          value={selectedPublisher}
          onChange={setSelectedPublisher}
          options={allPublishers.map((p) => ({ label: p, value: p }))}
          isClearable
        />
      </div>

      <div style={{ flex: 1, padding: 20 }}>
        <div
          style={{
            fontSize: 18,
            fontWeight: "bold",
            color: "#3fa9f5",
            marginBottom: 10,
          }}
        >
          {totalMindshare.toFixed(2)} total mobile mindSHARE
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
            gap: 16,
          }}
        >
          {filtered.map((game, index) => {
            const shareRatio = game.mindshare / maxMindshare;
            const barColor =
              shareRatio > 0.6
                ? "#3fa9f5"
                : shareRatio > 0.3
                ? "#67c4f5"
                : shareRatio > 0.1
                ? "#f5a623"
                : "#d94c3d";
            const tooltip = `${game.game}\nPublisher: ${
              game.publisher || "–"
            }\nTags: ${game.tags.join(", ") || "–"}`;

            return (
              <div
                key={index}
                title={tooltip}
                style={{
                  background: "#111",
                  padding: 10,
                  borderRadius: 12,
                  color: "#fff",
                  boxShadow: game.glow
                    ? `0 0 16px ${game.glow}`
                    : "0 4px 12px rgba(0,0,0,0.7)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.transform = "scale(1.03)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.transform = "scale(1)")
                }
              >
                <img
                  src={game.image}
                  alt={game.game}
                  style={{
                    width: "100%",
                    height: 100,
                    objectFit: "cover",
                    borderRadius: 10,
                    marginBottom: 8,
                  }}
                />
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontWeight: "bold",
                      fontSize: 14,
                      marginBottom: 4,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {game.game}
                  </div>

                  <div
                    style={{ color: "#3fa9f5", fontSize: 13, marginBottom: 4 }}
                  >
                    {(game.mindshare * 100).toFixed(3)}% mindSHARE
                  </div>

                  <div
                    style={{
                      width: "100%",
                      height: 6,
                      background: "#333",
                      borderRadius: 4,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.round(shareRatio * 100)}%`,
                        height: "100%",
                        background: barColor,
                        borderRadius: 4,
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default MobileMindshare;
